import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Check, X, Download, FileSpreadsheet, FileText, CheckCircle2, Lock, Phone } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Sessions are locked to the time of day on the *current* date. A teacher
// can still backfill or pre-fill any past/future date with either session.
// Cutoff is 12:30 local — between the AM block end (12:15) and PM start (13:00).
const SESSION_CUTOFF_MINUTES = 12 * 60 + 30;

function sessionForNow() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes() < SESSION_CUTOFF_MINUTES ? "morning" : "afternoon";
}

function todayISO() {
  // Local-date YYYY-MM-DD (not UTC) so the lock matches the user's wall clock
  const d = new Date();
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60_000).toISOString().slice(0, 10);
}

export default function TakeAttendance() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminLike = user?.role === "admin" || user?.role === "super_admin";
  const isReadOnly = user?.role === "super_admin";
  const cn = localStorage.getItem("sa_selected_class");
  const sec = localStorage.getItem("sa_selected_section");
  const isClassTeacher = useMemo(
    () => isAdminLike || (user?.class_teacher_of || []).some((a) => a.class_name === cn),
    [user, cn, isAdminLike]
  );
  const canMark = (isAdminLike || isClassTeacher) && !isReadOnly;
  const [classTeacher, setClassTeacher] = useState(null);
  const [date, setDate] = useState(todayISO());
  // Smart default: morning before 12:30 local, afternoon after
  const [session, setSession] = useState(() => sessionForNow());
  const [students, setStudents] = useState([]);
  const [statusMap, setStatusMap] = useState({}); // {student_id: "present"|"absent"}
  const [leaveMap, setLeaveMap] = useState({}); // {student_id: true} → informed/on leave, skip absence call
  const [leaveSuggested, setLeaveSuggested] = useState([]); // ids auto-detected from the Leaves module
  const [calledMorning, setCalledMorning] = useState([]); // ids already called for absence this morning
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [aiMap, setAiMap] = useState({}); // {student_id: {status, entry_time}} from face cameras
  const [aiOn, setAiOn] = useState(false);
  // Re-evaluate which session is "live" once a minute so the cutoff auto-flips
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Sessions are only locked when the user is taking attendance for *today*.
  // For past/future dates either session can be selected (back-fill or pre-fill).
  // Admin / super_admin always bypass the lock — they can edit any session any time.
  const isToday = date === todayISO();
  const liveSession = useMemo(() => sessionForNow(), [nowTick]);
  const lockedSession = isToday && !isAdminLike ? (liveSession === "morning" ? "afternoon" : "morning") : null;
  const lockReason = lockedSession === "afternoon"
    ? "Afternoon attendance unlocks at 12:30."
    : "Morning attendance is closed once 12:30 has passed.";
  // Teachers may only mark TODAY. Past + future dates are view-only for them
  // (admins keep full edit access). This drives every marking control below.
  const dateLocked = !isAdminLike && !isToday;
  // Marking is disabled when: not allowed to mark, a locked/past date, the
  // session is closed for now (teacher, today, off-session — still VIEWABLE),
  // or the sheet was already submitted (teachers get a single submission and
  // NO edit — only admins may correct a submitted sheet).
  const markingDisabled =
    !canMark ||
    dateLocked ||
    (isToday && !isAdminLike && session === lockedSession) ||
    (alreadySubmitted && !isAdminLike);

  useEffect(() => {
    if (!cn || !sec) navigate("/dashboard", { replace: true });
  }, [cn, sec, navigate]);

  useEffect(() => {
    if (!cn || !sec) return;
    api.get("/class-teacher", { params: { class_name: cn, section: sec } })
      .then((r) => setClassTeacher(r.data?.class_teacher || null))
      .catch(() => {});
  }, [cn, sec]);

  const loadData = async () => {
    if (!cn || !sec) return;
    setLoading(true);
    try {
      const [stuRes, attRes] = await Promise.all([
        api.get("/students", { params: { class_name: cn, section: sec } }),
        api.get("/attendance", { params: { class_name: cn, section: sec, date, session } }),
      ]);
      setStudents(stuRes.data);
      const map = {};
      const lmap = {};
      stuRes.data.forEach((s) => { map[s.id] = "present"; });
      if (attRes.data.submitted) {
        attRes.data.items.forEach((it) => { map[it.student_id] = it.status; if (it.on_leave) lmap[it.student_id] = true; });
        setAlreadySubmitted(true);
      } else {
        // Pre-tick "On leave" for students who applied/were granted leave for this date+session.
        (attRes.data.on_leave_suggested || []).forEach((id) => { lmap[id] = true; });
        setAlreadySubmitted(false);
      }
      setStatusMap(map);
      setLeaveMap(lmap);
      setLeaveSuggested(attRes.data.on_leave_suggested || []);
      setCalledMorning(attRes.data.called_in_morning || []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [cn, sec, date, session]);

  // AI camera check-ins for this class (read-only reference next to manual marking)
  useEffect(() => {
    if (!cn || !sec) return;
    api.get("/teacher/face-attendance", { params: { class_name: cn, section: sec, date } })
      .then(({ data }) => {
        const m = {};
        (data.rows || []).forEach((r) => {
          // Prefer the record matching the session being viewed (AM/PM)
          if (!m[r.student_id] || (r.session || "morning") === session) m[r.student_id] = r;
        });
        setAiMap(m);
        setAiOn(!!data.ai_enabled && (data.rows || []).length >= 0);
      })
      .catch(() => { setAiMap({}); setAiOn(false); });
  }, [cn, sec, date, session]);
  const aiCount = Object.keys(aiMap).length;

  const counts = useMemo(() => {
    const total = students.length;
    const present = Object.values(statusMap).filter((v) => v === "present").length;
    const absent = total - present;
    const onLeave = students.filter((s) => statusMap[s.id] !== "present" && leaveMap[s.id]).length;
    // Calls only for absent, not-on-leave students who weren't already called this morning.
    const calls = students.filter(
      (s) => statusMap[s.id] !== "present" && !leaveMap[s.id] && !calledMorning.includes(s.id)
    ).length;
    return { total, present, absent, onLeave, calls };
  }, [students, statusMap, leaveMap, calledMorning]);

  const setStatus = (id, status) => {
    setStatusMap((m) => ({ ...m, [id]: status }));
    if (status === "present") setLeaveMap((l) => { const n = { ...l }; delete n[id]; return n; });
  };
  const toggleLeave = (id) => setLeaveMap((l) => ({ ...l, [id]: !l[id] }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const items = students.map((s) => ({ student_id: s.id, status: statusMap[s.id] || "present", on_leave: !!leaveMap[s.id] }));
      const { data } = await api.post("/attendance", { class_name: cn, section: sec, date, session, items });
      let msg = data.edited ? "Attendance updated successfully." : "Attendance submitted successfully.";
      if (data.calls_scheduled > 0) {
        msg += ` Calling ${data.calls_scheduled} parent${data.calls_scheduled > 1 ? "s" : ""} of absent student${data.calls_scheduled > 1 ? "s" : ""}…`;
      }
      toast.success(msg);
      setReviewOpen(false);
      setConfirmOpen(false);
      setAlreadySubmitted(true);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get("/attendance/export", {
        params: { class_name: cn, section: sec, date, format: fmt },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `attendance_${cn}_${sec}_${date}_${session}.${fmt === "xlsx" ? "xlsx" : fmt}`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) {
      toast.error("Export failed: attendance may not be submitted yet.");
    }
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Take attendance</div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>
            Class {cn} · Section {sec}
          </h1>
          {alreadySubmitted && (
            <div className="mt-2 inline-flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 text-xs" data-testid="already-submitted-banner">
              <CheckCircle2 className="h-3.5 w-3.5" /> Attendance already submitted{markingDisabled ? " — view only." : " — you're editing it."}
            </div>
          )}
          {canMark && dateLocked && (
            <div className="mt-2 inline-flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-xs" data-testid="date-locked-banner">
              <Lock className="h-3.5 w-3.5" />
              Attendance can only be marked for today. This date is view-only — ask an admin to edit past records.
            </div>
          )}
          {!canMark && !isReadOnly && (
            <div className="mt-2 inline-flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-xs" data-testid="not-class-teacher-banner">
              <Lock className="h-3.5 w-3.5" />
              Only the class teacher{classTeacher?.name ? ` (${classTeacher.name})` : ""} can take attendance for this section. You have view-only access.
            </div>
          )}
          {canMark && classTeacher?.name && !isAdminLike && (
            <div className="mt-2 text-xs text-slate-500" data-testid="class-teacher-note">Class teacher: {classTeacher.name}</div>
          )}
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Session</label>
            <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1 h-11" data-testid="session-toggle">
              {["morning", "afternoon"].map((s) => {
                const locked = lockedSession === s;  // marking locked, but still viewable
                const active = session === s;
                const label = s === "morning" ? "Morning" : "Afternoon";
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSession(s)}
                    data-testid={`session-${s}`}
                    data-locked={locked ? "true" : "false"}
                    title={locked ? `${lockReason} You can view it but not edit.` : `Take ${label.toLowerCase()} attendance`}
                    aria-label={locked ? `${label} (view only — ${lockReason})` : label}
                    className={`px-4 h-9 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                      active
                        ? "bg-sky-500 text-white shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {locked && <Lock className="h-3 w-3" />}
                    {label}
                  </button>
                );
              })}
            </div>
            {lockedSession && (
              <div className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1" data-testid="session-lock-note">
                <Lock className="h-2.5 w-2.5" /> {lockReason} You can still view it.
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Date</label>
            <DatePicker value={date} onChange={setDate} quickPicks className="h-11 rounded-xl" data-testid="attendance-date-input"/>
          </div>
        </div>
      </div>

      {aiOn && aiCount > 0 && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 text-sky-800 px-4 py-2 text-xs font-medium flex items-center gap-2" data-testid="ai-summary-line">
          📷 AI camera detected <b>{aiCount}</b> of {students.length} students today — shown as chips below each name (reference only, your manual marking is the official record).
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500" data-testid="attendance-loading">Loading students…</div>
        ) : students.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No students in this class.</div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="attendance-list">
            {students.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-4 px-4 sm:px-6 py-3" data-testid={`student-row-${s.roll_number}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-sm font-medium">
                    {s.name.split(" ").map((p) => p[0]).slice(0,2).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{s.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                      <span>Roll #{s.roll_number}</span>
                      {aiMap[s.id] && (
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${aiMap[s.id].status === "late" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
                          title={`AI camera: ${aiMap[s.id].camera_name || ""} · confidence ${Math.round((aiMap[s.id].confidence || 0) * 100)}%`}
                          data-testid={`ai-chip-${s.roll_number}`}
                        >
                          📷 AI {aiMap[s.id].status === "late" ? "Late" : "Present"} {(aiMap[s.id].entry_time || "").slice(0, 5)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1">
                    <button
                      type="button"
                      onClick={() => setStatus(s.id, "present")}
                      disabled={markingDisabled}
                      data-testid={`toggle-present-${s.roll_number}`}
                      className={`px-4 h-9 rounded-full text-sm font-medium transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                        statusMap[s.id] === "present" ? "bg-emerald-500 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      <Check className="h-4 w-4" /> Present
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatus(s.id, "absent")}
                      disabled={markingDisabled}
                      data-testid={`toggle-absent-${s.roll_number}`}
                      className={`px-4 h-9 rounded-full text-sm font-medium transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                        statusMap[s.id] !== "present" ? "bg-rose-500 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      <X className="h-4 w-4" /> Absent
                    </button>
                  </div>
                  {/* Informed / on-leave — when checked, no absence call is placed. */}
                  <label
                    title="Student has informed / applied for leave — don't call parents"
                    className={`inline-flex items-center gap-1.5 text-xs font-medium select-none rounded-full px-2.5 py-1 border transition-colors ${
                      statusMap[s.id] === "present"
                        ? "opacity-40 cursor-not-allowed border-slate-200 text-slate-400"
                        : leaveMap[s.id]
                          ? "border-amber-300 bg-amber-50 text-amber-700 cursor-pointer"
                          : "border-slate-200 text-slate-500 hover:border-amber-300 hover:text-amber-700 cursor-pointer"
                    }`}
                    data-testid={`toggle-leave-${s.roll_number}`}
                  >
                    <Checkbox
                      checked={!!leaveMap[s.id]}
                      onCheckedChange={() => toggleLeave(s.id)}
                      disabled={markingDisabled || statusMap[s.id] === "present"}
                      className="h-4 w-4"
                    />
                    On leave
                    {leaveSuggested.includes(s.id) && !(alreadySubmitted && !leaveMap[s.id]) && (
                      <span className="ml-1 text-[9px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded bg-amber-200/70 text-amber-800" title="Auto-detected from a submitted leave request">applied</span>
                    )}
                  </label>
                  {calledMorning.includes(s.id) && statusMap[s.id] !== "present" && !leaveMap[s.id] && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-1 border border-sky-200 bg-sky-50 text-sky-700 whitespace-nowrap"
                      title="Parent was already called this morning — no call will be placed again this afternoon"
                      data-testid={`called-morning-${s.roll_number}`}
                    >
                      <Phone className="h-3 w-3" /> Called this morning
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          onClick={() => setReviewOpen(true)}
          disabled={students.length === 0 || markingDisabled}
          data-testid="review-btn"
          className="h-14 px-8 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white text-base font-medium w-full sm:w-auto"
        >
          {isReadOnly
            ? "Read-only — view only"
            : !canMark
              ? "Class teacher only"
              : dateLocked
                ? "View only — only today can be marked"
                : alreadySubmitted && !isAdminLike
                  ? "Submitted — view only"
                  : isToday && !isAdminLike && session === lockedSession
                    ? "View only — session closed"
                    : "Review Attendance →"}
        </Button>
      </div>

      {/* Review Modal */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{fontFamily:"Outfit"}}>Review attendance · {date} · {session === "morning" ? "Morning" : "Afternoon"}</DialogTitle>
            <DialogDescription>
              Class {cn} · Section {sec} — confirm before submitting.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <SummaryPill label="Total" value={counts.total} tone="slate" />
            <SummaryPill label="Present" value={counts.present} tone="emerald" />
            <SummaryPill label="Absent" value={counts.absent} tone="rose" />
          </div>
          <div className="max-h-80 overflow-y-auto rounded-xl border border-slate-200" data-testid="review-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-[0.15em]">
                <tr>
                  <th className="px-4 py-2 text-left">Roll</th>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {students.map((s) => (
                  <tr key={s.id}>
                    <td className="px-4 py-2 font-mono text-slate-700">{s.roll_number}</td>
                    <td className="px-4 py-2 text-slate-900">{s.name}</td>
                    <td className="px-4 py-2 text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        statusMap[s.id] === "present" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                      }`}>
                        {statusMap[s.id] === "present" ? "Present" : "Absent"}
                      </span>
                      {statusMap[s.id] !== "present" && leaveMap[s.id] && (
                        <span className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700" title="On leave — no call">On leave · no call</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setReviewOpen(false)} data-testid="review-edit-btn" className="rounded-full">Edit Attendance</Button>
            <Button onClick={() => setConfirmOpen(true)} data-testid="review-submit-btn" className="bg-sky-500 hover:bg-sky-600 rounded-full">Submit Attendance</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit attendance?</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to submit attendance for Class {cn} Section {sec} on {date}.{isAdminLike ? " You can re-edit later if needed." : " Please review carefully — this cannot be edited once submitted."}
              {counts.absent > 0 && (
                <span className="block mt-2 text-slate-600">
                  {counts.calls > 0
                    ? `${counts.calls} parent${counts.calls > 1 ? "s" : ""} of absent student${counts.calls > 1 ? "s" : ""} will be called${counts.onLeave > 0 ? `; ${counts.onLeave} on-leave student${counts.onLeave > 1 ? "s" : ""} skipped.` : "."}`
                    : `No absence calls will be placed${counts.onLeave > 0 ? ` (${counts.onLeave} marked on leave).` : "."}`}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); submit(); }}
              disabled={submitting}
              data-testid="confirm-submit-btn"
              className="bg-sky-500 hover:bg-sky-600"
            >
              {submitting ? "Submitting…" : "Yes, submit"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {alreadySubmitted && (
        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Export attendance</div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => download("csv")} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => download("xlsx")} data-testid="export-xlsx-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => download("pdf")} data-testid="export-pdf-btn">
              <FileText className="h-4 w-4 mr-1" /> PDF
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Stat({ label, value, tone = "slate", icon: Icon, testid }) {
  const toneMap = {
    slate: "bg-white border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  };
  return (
    <div className={`rounded-2xl border p-4 ${toneMap[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] opacity-70">{label}</div>
        {Icon && <Icon className="h-4 w-4 opacity-70" />}
      </div>
      <div className="text-3xl font-bold mt-1" style={{fontFamily:"Outfit"}}>{value}</div>
    </div>
  );
}

function SummaryPill({ label, value, tone }) {
  const map = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className={`rounded-xl px-4 py-3 ${map[tone]}`}>
      <div className="text-xs uppercase tracking-[0.15em] opacity-70">{label}</div>
      <div className="text-2xl font-bold" style={{fontFamily:"Outfit"}}>{value}</div>
    </div>
  );
}
